import React, { useEffect, useState } from 'react';
import { Button, Text, TextInput, View } from "../../Components/Themed"
import type { CompanyEmbedProps, CompanyProps, EmbedPropertyProps, EmbedProps, FocusPositionProps, MyPlayerProps, PlayerReferralProps, PromoProps } from '../../types';
import { PartnerPortalApi } from '../api';
import { ActivityIndicator, FlatList, ImageBackground, type ViewStyle } from 'react-native';
import { useColors } from '../../constants/useColors';
import ReferralSelector from './ReferralSelector';
import LeagueSelector from './LeagueSelector';
import { Switch, Toggle } from '../../Components';
import * as Clipboard from 'expo-clipboard';
import CompetitionSelector from './CompetitionSelector';
import SquaresSelector from './SquaresSelector';
import FlashSelector from './FlashSelector';
import FlashMarketSelector from './FlashMarketSelector';
import StringSelector from './StringSelector';

type EmbedManagerProps = {
    company_embed_id:string,
    me:MyPlayerProps,
    float?:boolean,
    onFocusPosition?:(pos:FocusPositionProps) => void,
    header_style?:ViewStyle,
    footer_style?:ViewStyle,
    company_id:string,
    onClose: () => void
}
const sections = ['header','embed','action','toggle','loading','name','properties','identifer','code']
const EmbedManager = ({ float, me, company_id, company_embed_id, onFocusPosition, onClose, header_style, footer_style }:EmbedManagerProps) => {
    const Colors = useColors();
    const [ size, setSize ] = useState({ height:0, width:0 });
    const [ active_toggle, setActiveToggle ] = useState('properties');
    const [ action_loading, setActionLoading ] = useState(false);
    const [ embed_data, setEmbedData ] = useState<{
        loading:boolean,
        company_embed?:CompanyEmbedProps,
        draft_company_embed?:CompanyEmbedProps,
        player_referrals:PlayerReferralProps[],
        promos:PromoProps[],
        company?:CompanyProps,
        embed?:EmbedProps,
        embed_properties:EmbedPropertyProps[]
    }>({
        loading:false,
        embed_properties: [],
        promos:[],
        player_referrals: []
    });
    const { loading, promos, player_referrals, embed, company_embed, embed_properties, draft_company_embed } = embed_data;

    const campaign_ids_str = draft_company_embed?.properties['flashcampaignids']
    let campaign_ids:string[] = []
    if(campaign_ids_str){ campaign_ids = campaign_ids_str.split(',') }


    const isValid = () => {
        if(!company_embed){ return }
        if(!company_embed.name){ return }
        let valid = true
        let props = company_embed.properties
        embed_properties.map(p => {
            if(!p.required){ return }
            let exists = props[p.property]
            if(!exists){ valid = false }
        });
        return valid
    }
    const is_valid = isValid();

    useEffect(() => {
        PartnerPortalApi.setEnvironment();
        getEmbedData(company_embed_id);
    },[company_embed_id, me.player_id]);

    const getEmbedData = async(id:string) => {
        setEmbedData({ ...embed_data, loading:true });
        const s_resp = await PartnerPortalApi.CompanyEmbedApi.getCompanyEmbedById(id);
        if(!s_resp){ setEmbedData({ ...embed_data, loading:false }); return }
        const s_comp = await PartnerPortalApi.CompanyApi.getCompanyById(company_id);
        const s_refer = await PartnerPortalApi.CompanyApi.getReferralCodesByCompanyId(company_id);
        setEmbedData({
            ...embed_data,
            embed: s_resp.embed,
            embed_properties: s_resp.embed_properties,
            company_embed: s_resp.company_embed,
            player_referrals: s_refer.player_referrals,
            promos: s_refer.promos,
            company: s_comp?.company,
            draft_company_embed: s_resp.company_embed,
            loading: false
        });
    }

    const handleSave = async(de:CompanyEmbedProps) => {
        setActionLoading(true);
        const new_embed = await PartnerPortalApi.CompanyEmbedApi.updateCompanyEmbed(de);
        if(!new_embed){ setActionLoading(false); return alert('Unable to update at this time.  Please try again later') }
        setEmbedData({
            ...embed_data,
            company_embed: new_embed,
            draft_company_embed: new_embed
        })
        setActionLoading(false);
    }

    const activate = async() => {
        if(!company_embed){ return }
        if(!is_valid){ return alert('Please complete all properties before activating') }
        setActionLoading(true);
        const new_embed = await PartnerPortalApi.CompanyEmbedApi.activateCompanyEmbed(company_embed.company_embed_id);
        if(!new_embed){ setActionLoading(false); return alert('Unable to activate at this time.  Please try again later') }
        setEmbedData({
            ...embed_data,
            company_embed: new_embed,
            draft_company_embed: new_embed
        })
        setActionLoading(false);
    }

    const archive = async() => {
        if(!company_embed){ return }
        setActionLoading(true);
        const new_embed = await PartnerPortalApi.CompanyEmbedApi.archiveCompanyEmbed(company_embed.company_embed_id);
        if(!new_embed){ setActionLoading(false); return alert('Unable to archive at this time.  Please try again later') }
        setEmbedData({
            ...embed_data,
            company_embed: new_embed,
            draft_company_embed: new_embed
        })
        setActionLoading(false);
    }


    const deactivate = async() => {
        if(!company_embed){ return }
        setActionLoading(true);
        const new_embed = await PartnerPortalApi.CompanyEmbedApi.deActivateCompanyEmbed(company_embed.company_embed_id);
        if(!new_embed){ setActionLoading(false); return alert('Unable to deactivate at this time.  Please try again later') }
        setEmbedData({
            ...embed_data,
            company_embed: new_embed,
            draft_company_embed: new_embed
        })
        setActionLoading(false);
    }


    const renderProperties = (data:{ item:EmbedPropertyProps, index:number }) => {
        if(!company_embed || !draft_company_embed){ return <></> }
        const value:string | undefined = draft_company_embed.properties[data.item.property]
        switch(data.item.component){
            case 'text_input':
                return (
                <View transparent style={{ borderBottomWidth:1, borderColor:Colors.borders.light, padding:10 }}>
                    <View transparent>
                        <Text theme='h1'>{data.item.label}</Text>
                        <Text theme='description' style={{ marginTop:3 }}>{data.item.description}</Text>
                    </View>
                    <TextInput
                        value={value}
                        onFocusPosition={onFocusPosition}
                        placeholder={data.item.property}
                        style={{ marginTop:10 }}
                        onChangeText={(text) => setEmbedData({ ...embed_data, draft_company_embed: { ...draft_company_embed, properties: { ...draft_company_embed.properties, [data.item.property]: text } } })}
                    />
                </View>
                )
            case 'string_selector':
                if(!data.item.options){ return <></> }
                return (
                    <View transparent style={{ borderBottomWidth:1, borderColor:Colors.borders.light, padding:10 }}>
                        <View transparent>
                            <Text theme='h1'>{data.item.label}</Text>
                            <Text theme='description' style={{ marginTop:3 }}>{data.item.description}</Text>
                        </View>
                        <View transparent style={{ marginTop:10 }}>
                            <StringSelector
                                active_value={value}
                                options={data.item.options}
                                onSelectOption={(option) => {
                                    setEmbedData({ ...embed_data, draft_company_embed: { ...draft_company_embed, properties: { ...draft_company_embed.properties, [data.item.property]: option?.value } } })
                                }}
                            />
                        </View>
                    </View>
                )
            case 'switch':
                //Switch is going to be either a 1 or a 0.  0 is off
                const boolean_value = value && value == '1' ? true : false
                return (
                    <View transparent style={{ flexDirection:'row', alignItems:'center', borderBottomWidth:1, borderColor:Colors.borders.light, padding:10 }}>
                        <View transparent style={{ flex:1, marginRight:10 }}>
                            <Text theme='h1'>{data.item.label}</Text>
                            <Text theme='description' style={{ marginTop:3 }}>{data.item.description}</Text>
                        </View>
                        <Switch
                            value={boolean_value}
                            switch_type='on_off'
                            onChange={(val) => {
                                let str_val = val ? '1' : '0'
                                setEmbedData({ ...embed_data, draft_company_embed: { ...draft_company_embed, properties: { ...draft_company_embed.properties, [data.item.property]: str_val } } })
                            }}
                        />
                    </View>
                )
            case 'refer_selector':
                const selected_referral = player_referrals.find(pr => pr.referral_code == value);
                return (
                    <View transparent style={{ borderBottomWidth:1, borderColor:Colors.borders.light, padding:10 }}>
                        <View transparent>
                            <Text theme='h1'>{data.item.label}</Text>
                            <Text theme='description' style={{ marginTop:3 }}>{data.item.description}</Text>
                        </View>
                        <View transparent style={{ marginTop:10 }}>
                            <ReferralSelector
                                promos={promos}
                                player_referrals={player_referrals}
                                active_referral={selected_referral}
                                onSelectReferral={(pr) => setEmbedData({ ...embed_data, draft_company_embed: { ...draft_company_embed, properties: { ...draft_company_embed.properties, [data.item.property]: pr?.referral_code } } })}
                            />
                        </View>
                    </View>
                )
            case 'league_selector':
                return (
                    <View transparent style={{ borderBottomWidth:1, borderColor:Colors.borders.light, padding:10 }}>
                        <View transparent>
                            <Text theme='h1'>{data.item.label}</Text>
                            <Text theme='description' style={{ marginTop:3 }}>{data.item.description}</Text>
                        </View>
                        <View transparent style={{ marginTop:10 }}>
                            <LeagueSelector
                                active_league_id={value}
                                onSelectLeague={(pr) => setEmbedData({ ...embed_data, draft_company_embed: { ...draft_company_embed, properties: { ...draft_company_embed.properties, [data.item.property]: pr?.league_id } } })}
                            />
                        </View>
                    </View>
                )
            case 'flash_campaign_selector':
                //Split out competition ids
                let fl_ids_string = value
                let fl_ids:string[] = fl_ids_string ? fl_ids_string.split(',') : []
                
                return (
                    <View transparent style={{ borderBottomWidth:1, borderColor:Colors.borders.light, padding:10 }}>
                        <View transparent>
                            <Text theme='h1'>{data.item.label}</Text>
                            <Text theme='description' style={{ marginTop:3 }}>{data.item.description}</Text>
                        </View>
                        <View transparent style={{ marginTop:10 }}>
                            <FlashSelector
                                company_id={company_id}
                                active_competitions={fl_ids}
                                onSelectCompetition={(competition) => {
                                    let new_value = fl_ids.concat(competition.poll_campaign_id).join(',');
                                    setEmbedData({ ...embed_data, draft_company_embed: { ...draft_company_embed, properties: { ...draft_company_embed.properties, [data.item.property]: new_value } } })
                                }}
                                onDeselectCompetition={(competition) => {
                                    let new_value = fl_ids.filter(id => id != competition.poll_campaign_id).join(',')
                                    setEmbedData({ ...embed_data, draft_company_embed: { ...draft_company_embed, properties: { ...draft_company_embed.properties, [data.item.property]: new_value } } })
                                }}
                            />
                        </View>
                    </View>
                )
            case 'flash_selector':
                //Split out competition ids
                let fm_ids_string = value
                let fm_ids:string[] = fm_ids_string ? fm_ids_string.split(',') : []
                return (
                    <View transparent style={{ borderBottomWidth:1, borderColor:Colors.borders.light, padding:10 }}>
                        <View transparent>
                            <Text theme='h1'>{data.item.label}</Text>
                            <Text theme='description' style={{ marginTop:3 }}>{data.item.description}</Text>
                        </View>
                        <View transparent style={{ marginTop:10 }}>
                            <FlashMarketSelector
                                company_id={company_id}
                                flash_campaigns={campaign_ids}
                                active_markets={fm_ids}
                                onSelectMarket={(market) => {
                                    let new_value = fm_ids.concat(market.poll_id).join(',');
                                    setEmbedData({ ...embed_data, draft_company_embed: { ...draft_company_embed, properties: { ...draft_company_embed.properties, [data.item.property]: new_value } } })
                                }}
                                onDeselectMarket={(market) => {
                                    let new_value = fm_ids.filter(id => id != market.poll_id).join(',')
                                    setEmbedData({ ...embed_data, draft_company_embed: { ...draft_company_embed, properties: { ...draft_company_embed.properties, [data.item.property]: new_value } } })
                                }}
                            />
                        </View>
                    </View>
                )
            case 'squares_selector':
                //Split out competition ids
                let sq_ids_string = value
                let sq_ids:string[] = sq_ids_string ? sq_ids_string.split(',') : []
                return (
                    <View transparent style={{ borderBottomWidth:1, borderColor:Colors.borders.light, padding:10 }}>
                        <View transparent>
                            <Text theme='h1'>{data.item.label}</Text>
                            <Text theme='description' style={{ marginTop:3 }}>{data.item.description}</Text>
                        </View>
                        <View transparent style={{ marginTop:10 }}>
                            <SquaresSelector
                                company_id={company_id}
                                active_competitions={sq_ids}
                                onSelectCompetition={(competition) => {
                                    let new_value = sq_ids.concat(competition.sq_comp_id).join(',');
                                    setEmbedData({ ...embed_data, draft_company_embed: { ...draft_company_embed, properties: { ...draft_company_embed.properties, [data.item.property]: new_value } } })
                                }}
                                onDeselectCompetition={(competition) => {
                                    let new_value = sq_ids.filter(id => id != competition.sq_comp_id).join(',')
                                    setEmbedData({ ...embed_data, draft_company_embed: { ...draft_company_embed, properties: { ...draft_company_embed.properties, [data.item.property]: new_value } } })
                                }}
                            />
                        </View>
                    </View>
                )
            case 'competition_selector':
                //Split out competition ids
                let comp_ids_string = value
                let comp_ids:string[] = comp_ids_string ? comp_ids_string.split(',') : []
                return (
                    <View transparent style={{ borderBottomWidth:1, borderColor:Colors.borders.light, padding:10 }}>
                        <View transparent>
                            <Text theme='h1'>{data.item.label}</Text>
                            <Text theme='description' style={{ marginTop:3 }}>{data.item.description}</Text>
                        </View>
                        <View transparent style={{ marginTop:10 }}>
                            <CompetitionSelector
                                company_id={company_id}
                                active_competitions={comp_ids}
                                onSelectCompetition={(competition) => {
                                    let new_value = comp_ids.concat(competition.competition_id).join(',');
                                    setEmbedData({ ...embed_data, draft_company_embed: { ...draft_company_embed, properties: { ...draft_company_embed.properties, [data.item.property]: new_value } } })
                                }}
                                onDeselectCompetition={(competition) => {
                                    let new_value = comp_ids.filter(id => id != competition.competition_id).join(',')
                                    setEmbedData({ ...embed_data, draft_company_embed: { ...draft_company_embed, properties: { ...draft_company_embed.properties, [data.item.property]: new_value } } })
                                }}
                            />
                        </View>
                    </View>
                )
            default:
                return <></>
        }
    }

    const renderSections = (data:{ item:string, index:number }) => {
        switch(data.item){
            
            case 'header':
                if(!embed){ return <></> }
                return (
                    <ImageBackground
                        source={{ uri: embed.image.url }}
                        style={{ width: size.width, height:225, justifyContent:'flex-end', overflow:'hidden', ...header_style}}>
                        <View type='dark_blur' style={{ padding:20 }}>
                            <Text theme='h1' size={36}>{embed.name}</Text>
                            <Text theme='description' size={16} style={{ marginTop:3 }}>{embed.description}</Text>
                        </View>
                    </ImageBackground>
                )
            case 'action':
                if(!draft_company_embed){ return <></> }
                switch(draft_company_embed.status){
                    case 'pending':
                        return (
                            <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10, borderTopRightRadius:8, borderTopLeftRadius:8 }}>
                              <View transparent style={{ flex:1, marginRight:5 }}>
                                <Text theme='h1'>Pending</Text>
                                <Text theme='description' style={{ marginTop:3 }}>Once ready - press activate for the embed to become usable</Text>
                              </View>
                              <Button
                                title='ACTIVATE'
                                type='success'
                                loading={action_loading}
                                style={{ padding:10, opacity: is_valid ? 1 : 0.5 }}
                                onPress={() => activate()}
                              />
                            </View>
                        )
                    case 'inactive':
                        return (
                            <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10, borderTopRightRadius:8, borderTopLeftRadius:8 }}>
                              <View transparent style={{ flex:1, marginRight:5 }}>
                                <Text theme='h1'>Inactive</Text>
                                <Text theme='description' style={{ marginTop:3 }}>Press activate to reactive it or archive it</Text>
                              </View>
                              <Button
                                title='ACTIVATE'
                                type='success'
                                loading={action_loading}
                                style={{ padding:10, marginRight:5 }}
                                onPress={() => activate()}
                              />
                              <Button
                                title='ARCHIVE'
                                type='error'
                                loading={action_loading}
                                style={{ padding:10 }}
                                onPress={() => archive()}
                              />
                            </View>
                        )
                    case 'active':
                         return (
                            <View type='header' style={{ flexDirection:'row', alignItems:'center', padding:10, borderTopRightRadius:8, borderTopLeftRadius:8 }}>
                              <View transparent style={{ flex:1, marginRight:5 }}>
                                <Text theme='h1'>Deactive</Text>
                                <Text theme='description' style={{ marginTop:3 }}>Your embed is active! Press deactivate to stop it</Text>
                              </View>
                              <Button
                                title='DEACTIVATE'
                                type='error'
                                loading={action_loading}
                                style={{ padding:10 }}
                                onPress={() => deactivate()}
                              />
                            </View>
                        )
                    default: return <></>
                }
            case 'toggle':
                return (
                    <View style={{ padding:10, paddingBottom:0 }}>
                        <Toggle
                            options={[
                                { key: 'properties', label: 'Properties' },
                                { key: 'embed', label: 'Embed It!' }
                            ]}
                            selected_option={active_toggle}
                            onSelectOption={(key) => setActiveToggle(key)}
                        />
                    </View>
                )
            case 'name':
                if(active_toggle != 'properties'){ return <></> }
                if(!draft_company_embed){ return <></> }
                return (
                    <View transparent style={{ borderBottomWidth:1, borderColor:Colors.borders.light, padding:20 }}>
                        <View transparent>
                            <Text theme='h1'>Embed Name</Text>
                            <Text theme='description' style={{ marginTop:3 }}>Give this embed a memorable name</Text>
                        </View>
                        <TextInput
                            value={draft_company_embed?.name}
                            onFocusPosition={onFocusPosition}
                            placeholder={'Enter Embed Name...'}
                            style={{ marginTop:10 }}
                            onChangeText={(text) => setEmbedData({ ...embed_data, draft_company_embed: { ...draft_company_embed, name: text } })}
                        />
                    </View>
                )
            case 'properties':
                if(active_toggle != 'properties'){ return <></> }
                return (
                    <View transparent style={{ padding:10 }}>
                        <FlatList
                            key='embed_properties'
                            keyExtractor={item => item.embed_property_id.toString()}
                            data={embed_properties.sort((a,b) => a.priority - b.priority)}
                            renderItem={renderProperties}
                        />
                    </View>
                )
            case 'identifer':
                if(active_toggle != 'embed'){ return <></> }
                if(!company_embed){ return <></> }
                return (
                    <View transparent style={{ padding:20 }}>
                        <Text theme='h1' size={18}>Your Embed Identifier</Text>
                        <Text theme='description'>Each embed has a unique identifier. Paste this into the embed code or copy the entire snipped below</Text>
                        <Button float style={{ padding:10, marginTop:10 }}>
                            <Text theme='h1' selectable color={Colors.text.action}>{company_embed.identifier}</Text>
                            <Text theme='description' style={{ marginTop:3 }}>Press / click to copy</Text>
                        </Button>
                    </View>
                )
            case 'loading':
                if(!loading){ return <></> }
                return (
                    <ActivityIndicator style={{ padding:20, alignSelf: 'center' }} size='large' />
                )
            case 'code':
                if(active_toggle != 'embed'){ return <></> }
                if(!company_embed){ return <></> }

                const embed_code = `
                    <div id='bettoredge' style='height:100%; width:100%;' data-identifier='${company_embed.identifier}'></div>
                    <script src='https://embed.bettoredge.com/common.bundle.js'></script>
                    <script src='https://embed.bettoredge.com/embed.bundle.js'></script>
                `

                return (
                    <View transparent style={{ padding:20 }}>
                        <Text theme='h1' size={18}>Your Embed Code</Text>
                        <Text theme='description'>Copy the code below and add it to your website.  Adjust styling as you like.  Embeds will fill 100% of the parent container</Text>
                        <Button 
                            transparent style={{ marginTop:10, padding:10, backgroundColor:Colors.views.illiquidMarket }}
                            onPress={async() => {
                                await Clipboard.setStringAsync(embed_code);
                                alert('Code copied successfully')
                            }}
                            >
                            <Text theme='description' selectable>{`<div id='bettoredge' style='height:100%; width:100%;' data-identifier='${company_embed.identifier}'></div>`}</Text>
                            <Text theme='description' style={{ marginTop:10 }} selectable>{`<script src='https://embed.bettoredge.com/common.bundle.js'></script>`}</Text>
                            <Text theme='description' style={{ marginTop:3 }} selectable>{`<script src='https://embed.bettoredge.com/embed.bundle.js'></script>`}</Text>
                            <Text theme='action' style={{ marginTop:25 }} selectable>Press / click to copy</Text>
                        </Button>
                    </View>
                )
            default: return <></>
        }
    }
    const is_changed = JSON.stringify(company_embed) != JSON.stringify(draft_company_embed) ? true : false
    return (
        <View float={float} style={{ flex:1 }} onLayout={(ev) => {
            const { width, height } = ev.nativeEvent.layout;
            setSize({ width, height });

        }}>
            <FlatList
                data={sections}
                key={'embed_sections'}
                refreshing={loading}
                onRefresh={() => getEmbedData(company_embed_id)}
                keyExtractor={item => item}
                renderItem={renderSections}
            />
            {company_embed?.status == 'archived' ? 
            <View type='blur' style={{ position:'absolute', top:0, left:0, right:0, bottom:0, justifyContent:'center', alignItems:'center' }}>
                <View float style={{ height:200, width:200, justifyContent:'center', alignItems:'center' }}>
                    <Text theme='h1' textAlign='center'>EMBED HAS BEEN ARCHIVED</Text>
                </View>
            </View>
            :<></>}
            {draft_company_embed ? 
            <View type='footer' style={{ flexDirection:'row', alignItems:'center', padding:10, ...footer_style }}>
                <Button
                    title='CLOSE'
                    type='close'
                    style={{ flex:1 }}
                    onPress={() => onClose()}
                />
                {is_changed}
                <Button
                    title='SAVE'
                    type='success'
                    loading={action_loading}
                    style={{ flex:3, marginLeft:5, opacity: is_changed && !action_loading ? 1 : 0.5 }}
                    disabled={!is_changed || action_loading}
                    onPress={() => handleSave(draft_company_embed)}
                />
            </View>
            :<></>}
        </View>
    )
}

export default EmbedManager